<script setup>
import { useForm, Link } from '@inertiajs/vue3'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import UiButton from '@/Components/UI/UiButton.vue'
import StickyActionBar from '@/Components/UI/StickyActionBar.vue'
import Icon from '@/Components/UI/Icon.vue'

defineOptions({ layout: AppLayout })

const props = defineProps({
  user:   { type: Object, required: true },
  roles:  { type: Array,  required: true },
  errors: { type: Object, default: () => ({}) },
})

const isNew = !props.user.id

const form = useForm({
  first_name: props.user.first_name,
  last_name:  props.user.last_name,
  email:      props.user.email,
  role:       props.user.role,
  password:   '',
})

function submit() {
  if (isNew) {
    form.transform((data) => ({ user: data })).post('/users')
  } else {
    form.transform((data) => ({ user: data })).patch(`/users/${props.user.id}`)
  }
}
</script>

<template>
  <PageShell :title="isNew ? 'New User' : 'Edit User'">
    <div class="max-w-2xl mx-auto w-full">
      <Link
        href="/users"
        class="inline-flex items-center text-[color:var(--muted)] hover:text-[color:var(--ink)] transition-colors mb-4"
        aria-label="Back"
      >
        <Icon name="chev-l" :size="18" />
      </Link>

      <form
        id="user-form"
        @submit.prevent="submit"
        class="rounded-[var(--radius-lg)] border border-[color:var(--line)] bg-[color:var(--surface)] p-6 space-y-5"
      >
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-[color:var(--inkSoft)] mb-1">First name</label>
            <input
              v-model="form.first_name"
              type="text"
              required
              class="w-full rounded-[var(--radius-md)] border bg-[color:var(--surface)] text-[color:var(--ink)] px-3 py-2 text-sm focus:outline-2 focus:outline-[color:var(--accent)]"
              :class="form.errors.first_name ? 'border-[color:var(--danger)]' : 'border-[color:var(--line)]'"
            />
            <p v-if="form.errors.first_name" class="mt-1 text-xs text-[color:var(--danger)]">{{ form.errors.first_name }}</p>
          </div>
          <div>
            <label class="block text-sm font-medium text-[color:var(--inkSoft)] mb-1">Last name</label>
            <input
              v-model="form.last_name"
              type="text"
              required
              class="w-full rounded-[var(--radius-md)] border bg-[color:var(--surface)] text-[color:var(--ink)] px-3 py-2 text-sm focus:outline-2 focus:outline-[color:var(--accent)]"
              :class="form.errors.last_name ? 'border-[color:var(--danger)]' : 'border-[color:var(--line)]'"
            />
            <p v-if="form.errors.last_name" class="mt-1 text-xs text-[color:var(--danger)]">{{ form.errors.last_name }}</p>
          </div>
        </div>

        <div>
          <label class="block text-sm font-medium text-[color:var(--inkSoft)] mb-1">Email</label>
          <input
            v-model="form.email"
            type="email"
            required
            class="w-full rounded-[var(--radius-md)] border bg-[color:var(--surface)] text-[color:var(--ink)] px-3 py-2 text-sm focus:outline-2 focus:outline-[color:var(--accent)]"
            :class="form.errors.email ? 'border-[color:var(--danger)]' : 'border-[color:var(--line)]'"
          />
          <p v-if="form.errors.email" class="mt-1 text-xs text-[color:var(--danger)]">{{ form.errors.email }}</p>
        </div>

        <div>
          <label class="block text-sm font-medium text-[color:var(--inkSoft)] mb-1">Role</label>
          <select
            v-model="form.role"
            class="w-full rounded-[var(--radius-md)] border border-[color:var(--line)] bg-[color:var(--surface)] text-[color:var(--ink)] px-3 py-2 text-sm focus:outline-2 focus:outline-[color:var(--accent)]"
          >
            <option v-for="r in roles" :key="r" :value="r">
              {{ r.charAt(0).toUpperCase() + r.slice(1) }}
            </option>
          </select>
        </div>

        <div>
          <label class="block text-sm font-medium text-[color:var(--inkSoft)] mb-1">
            Password <span v-if="!isNew" class="text-[color:var(--muted)] font-normal">(leave blank to keep current)</span>
          </label>
          <input
            v-model="form.password"
            type="password"
            :required="isNew"
            minlength="8"
            class="w-full rounded-[var(--radius-md)] border bg-[color:var(--surface)] text-[color:var(--ink)] px-3 py-2 text-sm focus:outline-2 focus:outline-[color:var(--accent)]"
            :class="form.errors.password ? 'border-[color:var(--danger)]' : 'border-[color:var(--line)]'"
          />
          <p v-if="form.errors.password" class="mt-1 text-xs text-[color:var(--danger)]">{{ form.errors.password }}</p>
        </div>
      </form>

      <StickyActionBar>
        <UiButton
          type="submit"
          form="user-form"
          variant="primary"
          :loading="form.processing"
        >
          {{ form.processing ? 'Saving…' : (isNew ? 'Create user' : 'Save changes') }}
        </UiButton>
      </StickyActionBar>
    </div>
  </PageShell>
</template>
